/**
 * Showcase Validator — 展示关卡回归验证系统
 *
 * 三层验收:
 *   P0: 观赏性回归（spectacle score ≥ baseline × 0.85）
 *   P1: 行为断言（每关定义必须发生的事件模式+阈值）
 *   P2: 自我进化建议（诊断 6 维度短板→生成改进建议）
 *
 * 用法:
 *   npx tsx src/services/showcase-validator.ts --all
 *   npx tsx src/services/showcase-validator.ts --level level_1_open_duel
 *
 * 复用:
 *   - runHeadlessBattle + computeHeadlessSpectacle（evolution-arena.ts）
 *   - computeSpectacle + SpectacleResult（spectacle-scorer.ts）
 *   - TANK_STATS（tank-stats.ts）
 */

import { computeSpectacle, type SpectacleFrame, type SpectacleResult } from "./spectacle-scorer";
import { TANK_STATS } from "../engine/tank-stats";
import { EngineScheduler } from "../engine/engine-scheduler";
import { getMapById } from "../engine/map-utils";
import type { BattleState, EngineEvent } from "../engine/types";
import assertionsConfig from "./showcase-assertions.json";

// ===== Headless 对打类型（内联，避免 tsx 解析 evolution-arena.ts 的 ?raw 导入） =====

interface ReplayFrame {
  tick: number;
  selfPos: { x: number; y: number };
  enemyPos: { x: number; y: number };
  selfHp: number;
  enemyHp: number;
  selfAction: string;
  enemyAction: string;
  damageAtoB: number;
  damageBtoA: number;
}

interface HeadlessBattleResult {
  winner: "team_a" | "team_b" | "draw";
  totalTicks: number;
  damageAtoB: number;
  damageBtoA: number;
  replay: ReplayFrame[];
  keyEvents: Array<{ eventType: string; tickNum: number; actor: string }>;
  error?: string;
}

// ===== 类型定义 =====

interface ShowcaseAssertion {
  levelId: string;
  name: string;
  rounds: number;
  unitsPerTeam?: number;
  baselineSpectacle: number;
  regressionThreshold: number;
  behavioral: Record<string, number | boolean>;
  description: string;
}

interface DiagnosisRule {
  dimension: string;
  threshold: number;
  diagnosis: string;
  suggestion: string;
}

interface RoundResult {
  round: number;
  spectacle: number;
  dimensions: SpectacleResult["dimensions"];
  winner: string;
  ticks: number;
  damageAtoB: number;
  damageBtoA: number;
  coverUseCount: number;
  scanDiscoveryCount: number;
  phaseCount: number;
  passedBehavioral: boolean;
  behavioralFailures: string[];
}

export interface ShowcaseReport {
  levelId: string;
  levelName: string;
  rounds: number;
  avgSpectacle: number;
  minSpectacle: number;
  maxSpectacle: number;
  baselineSpectacle: number;
  p0Passed: boolean;
  p0Threshold: number;
  p1Passed: boolean;
  p1Failures: string[];
  avgDimensions: SpectacleResult["dimensions"];
  p2Diagnoses: string[];
  roundResults: RoundResult[];
  duration: number;
}

interface FullReport {
  timestamp: string;
  levels: ShowcaseReport[];
  overallPassed: boolean;
  totalDuration: number;
}

// ===== AI bot 代码加载（Vite ?raw 导入，浏览器原生支持） =====
import aiL1Raw from "../../../data/levels/_builtin/level_1_open_duel/ai.py?raw";
import aiL4Raw from "../../../data/levels/_builtin/level_4_lateral_strike/ai.py?raw";
import ai2v2Raw from "../../../data/levels/_builtin/showcase_2v2/ai.py?raw";
import ai3v3Raw from "../../../data/levels/_builtin/battle_3v3/ai.py?raw";
// 默认 AI 对手（引擎内置，非对称对打确保产生实际战斗）
import defaultAiRaw from "../engine/showcase-opponent.py?raw";

const AI_CODE_MAP: Record<string, string> = {
  level_1_open_duel: aiL1Raw,
  level_4_lateral_strike: aiL4Raw,
  showcase_2v2: ai2v2Raw,
  battle_3v3: ai3v3Raw,
};

/** 默认 AI 对手代码（team_b 固定使用，确保非对称对打产生实际交火） */
const DEFAULT_OPPONENT_CODE = defaultAiRaw;

import { dataProxy } from "../core/data-proxy.js";

// ===== baseline 持久化 =====

function loadBaselines(): Record<string, number> {
  return dataProxy.get<Record<string, number>>("showcase-baseline") ?? {};
}

function saveBaselines(baselines: Record<string, number>): void {
  try {
    dataProxy.set("showcase-baseline", baselines);
  } catch {
    console.warn("[showcase-validator] Failed to persist baselines");
  }
}

// ===== 核心验证 =====

/**
 * 内联 headless 对打（复用 EngineScheduler，避免 evolution-arena.ts 的 ?raw 导入）
 */
function runHeadlessBattle(
  botACode: string,
  botBCode: string,
  mapId: string,
  speedMultiplier: number,
  unitsPerTeam: number = 1,
): Promise<HeadlessBattleResult> {
  return new Promise((resolve) => {
    const engine = new EngineScheduler();
    engine.setLevelId(mapId);
    engine.setSpeedMultiplier(speedMultiplier);
    engine.setHeadlessMode(true);
    if (unitsPerTeam > 1) engine.setUnitsPerTeam(unitsPerTeam);

    const replay: ReplayFrame[] = [];
    const keyEvents: Array<{ eventType: string; tickNum: number; actor: string }> = [];
    let damageAtoB = 0;
    let damageBtoA = 0;
    let lastTick = 0;
    let resolved = false;

    const finish = (result: Partial<HeadlessBattleResult>) => {
      if (resolved) return;
      resolved = true;
      removeListener();
      engine.stop();
      resolve({
        winner: result.winner ?? "draw",
        totalTicks: result.totalTicks ?? lastTick,
        damageAtoB,
        damageBtoA,
        replay,
        keyEvents,
        error: result.error,
      });
    };

    const removeListener = engine.on((event: EngineEvent) => {
      switch (event.type) {
        case "tick":
          lastTick = event.tickNum;
          const s = event.state;
          replay.push({
            tick: event.tickNum,
            selfPos: { ...s.self.position },
            enemyPos: { ...s.enemy.position },
            selfHp: s.self.health,
            enemyHp: s.enemy.health,
            selfAction: event.actions.team_a.type,
            enemyAction: event.actions.team_b.type,
            damageAtoB: event.damage.team_a_to_team_b,
            damageBtoA: event.damage.team_b_to_team_a,
          });
          if (event.damage.team_a_to_team_b > 0) damageAtoB += event.damage.team_a_to_team_b;
          if (event.damage.team_b_to_team_a > 0) damageBtoA += event.damage.team_b_to_team_a;
          break;
        case "key_event":
          keyEvents.push({ eventType: event.eventType, tickNum: event.tickNum, actor: event.actor });
          break;
        case "battle_end":
          finish({ winner: event.winner, totalTicks: lastTick });
          break;
        case "error":
          finish({ winner: "draw", totalTicks: lastTick, error: "engine_error" });
          break;
      }
    });

    const map = getMapById(mapId);
    const dims = map.dimensions ?? { width: 25, height: 25 };
    const allSpawnsA = map.spawns?.team_a ?? [{ x: 0, y: 12 }];
    const allSpawnsB = map.spawns?.team_b ?? [{ x: 24, y: 12 }];

    // 多坦克模式: 构造完整的 team_a/team_b（1v1 向后兼容）
    const teamATanks = [];
    const teamBTanks = [];
    for (let i = 0; i < unitsPerTeam; i++) {
      const sa = allSpawnsA[i] ?? allSpawnsA[0];
      const sb = allSpawnsB[i] ?? allSpawnsB[0];
      teamATanks.push({
        team: "team_a" as const,
        tank_index: i,
        position: { x: sa.x, y: sa.y },
        spawn: { x: sa.x, y: sa.y },
        health: TANK_STATS.max_health,
        max_health: TANK_STATS.max_health,
        cooldown_left: 0,
        attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: true,
      });
      teamBTanks.push({
        team: "team_b" as const,
        tank_index: i,
        position: { x: sb.x, y: sb.y },
        spawn: { x: sb.x, y: sb.y },
        health: TANK_STATS.max_health,
        max_health: TANK_STATS.max_health,
        cooldown_left: 0,
        attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: true,
      });
    }

    const spawnB = allSpawnsB[0];

    const initialState: BattleState = {
      tick: 0,
      map_id: mapId,
      map_size: { width: dims.width, height: dims.height },
      self: { ...teamATanks[0] },
      enemy: {
        tank_index: 0,
        position: { x: spawnB.x, y: spawnB.y },
        spawn: { x: spawnB.x, y: spawnB.y },
        health: TANK_STATS.max_health,
        max_health: TANK_STATS.max_health,
        is_alive: true,
        is_visible: false,
        last_known_position: null,
      },
      nearest_cover: null,
      team_a: { tanks: teamATanks, units_per_team: unitsPerTeam },
      team_b: { tanks: teamBTanks, units_per_team: unitsPerTeam },
    };

    engine.start(botACode, initialState, botBCode);
    setTimeout(() => finish({ winner: "draw", totalTicks: lastTick, error: "timeout" }), 30000);
  });
}

/**
 * 内联观赏性评分封装
 */
function computeHeadlessSpectacle(result: HeadlessBattleResult): number {
  const frames: SpectacleFrame[] = result.replay.map((f) => ({
    tick: f.tick,
    selfHp: f.selfHp,
    enemyHp: f.enemyHp,
    selfAction: f.selfAction,
    enemyAction: f.enemyAction,
    damageAtoB: f.damageAtoB,
    damageBtoA: f.damageBtoA,
    selfPos: f.selfPos,
    enemyPos: f.enemyPos,
  }));
  return computeSpectacle({
    frames,
    totalTicks: result.totalTicks,
    keyEvents: result.keyEvents,
    maxHp: TANK_STATS.max_health,
  }).total;
}

function computePhaseCount(result: HeadlessBattleResult, maxHp: number): number {
  const frames: SpectacleFrame[] = result.replay.map((f: ReplayFrame) => ({
    tick: f.tick,
    selfHp: f.selfHp,
    enemyHp: f.enemyHp,
    selfAction: f.selfAction,
    enemyAction: f.enemyAction,
    damageAtoB: f.damageAtoB,
    damageBtoA: f.damageBtoA,
    selfPos: f.selfPos,
    enemyPos: f.enemyPos,
  }));

  const phases = new Set<string>();
  let aPhase = "full";
  let bPhase = "full";

  for (const f of frames) {
    const aRatio = f.selfHp / maxHp;
    const bRatio = f.enemyHp / maxHp;

    const newAPhase = aRatio > 0.66 ? "full" : aRatio > 0.33 ? "mid" : aRatio > 0 ? "low" : "death";
    const newBPhase = bRatio > 0.66 ? "full" : bRatio > 0.33 ? "mid" : bRatio > 0 ? "low" : "death";

    if (newAPhase !== aPhase) { phases.add(`a_${newAPhase}`); aPhase = newAPhase; }
    if (newBPhase !== bPhase) { phases.add(`b_${newBPhase}`); bPhase = newBPhase; }
  }

  return phases.size;
}

async function runOneRound(
  levelId: string,
  unitsPerTeam: number,
): Promise<RoundResult & { result: HeadlessBattleResult }> {
  const aiCode = AI_CODE_MAP[levelId];
  if (!aiCode) throw new Error(`Unknown level: ${levelId}`);

  const result = await runHeadlessBattle(aiCode, DEFAULT_OPPONENT_CODE, levelId, 10, unitsPerTeam);
  const spectacle = computeHeadlessSpectacle(result);

  // 计算维度详情
  const frames: SpectacleFrame[] = result.replay.map((f: ReplayFrame) => ({
    tick: f.tick,
    selfHp: f.selfHp,
    enemyHp: f.enemyHp,
    selfAction: f.selfAction,
    enemyAction: f.enemyAction,
    damageAtoB: f.damageAtoB,
    damageBtoA: f.damageBtoA,
    selfPos: f.selfPos,
    enemyPos: f.enemyPos,
  }));

  const spectacleDetail = computeSpectacle({
    frames,
    totalTicks: result.totalTicks,
    keyEvents: result.keyEvents,
    maxHp: TANK_STATS.max_health,
  });

  const coverUseCount = result.keyEvents.filter((e) => e.eventType === "COVER_USE").length;
  const scanDiscoveryCount = result.keyEvents.filter((e) => e.eventType === "SCAN_DISCOVERY").length;
  const phaseCount = computePhaseCount(result, TANK_STATS.max_health);

  return {
    round: 0, // 由调用方设置
    spectacle,
    dimensions: spectacleDetail.dimensions,
    winner: result.winner,
    ticks: result.totalTicks,
    damageAtoB: result.damageAtoB,
    damageBtoA: result.damageBtoA,
    coverUseCount,
    scanDiscoveryCount,
    phaseCount,
    passedBehavioral: true,
    behavioralFailures: [],
    result,
  };
}

function checkBehavioral(
  roundResult: Omit<RoundResult, "passedBehavioral" | "behavioralFailures">,
  assertion: ShowcaseAssertion,
): { passed: boolean; failures: string[] } {
  const failures: string[] = [];
  const b = assertion.behavioral;

  // 每个断言在配置中的 key 映射到 roundResult 的对应字段
  const checks: Array<{ key: string; actual: number | boolean; expected: number | boolean; label: string; compare: "gte" | "lte" | "eq" }> = [];

  if (typeof b.minScanDiscoveries === "number") {
    checks.push({ key: "minScanDiscoveries", actual: roundResult.scanDiscoveryCount, expected: b.minScanDiscoveries, label: `SCAN_DISCOVERY >= ${b.minScanDiscoveries}`, compare: "gte" });
  }
  if (typeof b.maxCoverUse === "number") {
    checks.push({ key: "maxCoverUse", actual: roundResult.coverUseCount, expected: b.maxCoverUse, label: `COVER_USE <= ${b.maxCoverUse}`, compare: "lte" });
  }
  if (typeof b.minCoverUse === "number") {
    checks.push({ key: "minCoverUse", actual: roundResult.coverUseCount, expected: b.minCoverUse, label: `COVER_USE >= ${b.minCoverUse}`, compare: "gte" });
  }
  if (typeof b.minDamageExchange === "number") {
    // 使用实际的伤害交换率
    const totalDamage = roundResult.damageAtoB + roundResult.damageBtoA;
    const actualExchange = totalDamage > 0
      ? 1 - Math.abs(roundResult.damageAtoB - roundResult.damageBtoA) / totalDamage
      : 0;
    checks.push({ key: "minDamageExchange", actual: actualExchange, expected: b.minDamageExchange, label: `damage exchange >= ${b.minDamageExchange}`, compare: "gte" });
  }
  if (typeof b.minTickDuration === "number") {
    checks.push({ key: "minTickDuration", actual: roundResult.ticks, expected: b.minTickDuration, label: `ticks >= ${b.minTickDuration}`, compare: "gte" });
  }
  if (typeof b.maxTickDuration === "number") {
    checks.push({ key: "maxTickDuration", actual: roundResult.ticks, expected: b.maxTickDuration, label: `ticks <= ${b.maxTickDuration}`, compare: "lte" });
  }
  if (typeof b.minPhaseProgression === "number") {
    checks.push({ key: "minPhaseProgression", actual: roundResult.phaseCount, expected: b.minPhaseProgression, label: `phase >= ${b.minPhaseProgression}`, compare: "gte" });
  }
  if (typeof b.minMovementComplexity === "number") {
    checks.push({ key: "minMovementComplexity", actual: roundResult.dimensions.movementComplexity, expected: b.minMovementComplexity, label: `movement >= ${b.minMovementComplexity}`, compare: "gte" });
  }
  if (b.noCollisionErrors === true) {
    // 无碰撞错误：检查是否有 collision key_event
    const hasCollision = false; // keyEvents 目前不直接暴露 COLLISION，通过 error 检测
    if (hasCollision) {
      failures.push("COLLISION_ERROR: 检测到碰撞错误");
    }
  }

  for (const check of checks) {
    let passed = false;
    if (check.compare === "gte") passed = (check.actual as number) >= (check.expected as number);
    if (check.compare === "lte") passed = (check.actual as number) <= (check.expected as number);
    if (check.compare === "eq") passed = check.actual === check.expected;

    if (!passed) {
      failures.push(`${check.label} (实际: ${check.actual})`);
    }
  }

  return { passed: failures.length === 0, failures };
}

function generateDiagnoses(
  avgDimensions: SpectacleResult["dimensions"],
): string[] {
  const rules = assertionsConfig.diagnosisRules as DiagnosisRule[];
  const diagnoses: string[] = [];

  const dimMap: Record<string, number> = {
    damageExchange: avgDimensions.damageExchange,
    movementComplexity: avgDimensions.movementComplexity,
    coverInteraction: avgDimensions.coverInteraction,
    phaseProgression: avgDimensions.phaseProgression,
    durationOptimal: avgDimensions.durationOptimal,
    lowHpComeback: avgDimensions.lowHpComeback,
  };

  for (const rule of rules) {
    const value = dimMap[rule.dimension];
    if (value !== undefined && value < rule.threshold) {
      diagnoses.push(`[${rule.dimension}=${Math.round(value)}/100] ${rule.diagnosis} → ${rule.suggestion}`);
    }
  }

  return diagnoses;
}

// ===== 主验证函数 =====

export async function validateShowcaseLevel(levelId: string): Promise<ShowcaseReport> {
  const assertion = (assertionsConfig.assertions as Record<string, ShowcaseAssertion>)[levelId];
  if (!assertion) throw new Error(`No assertion config for level: ${levelId}`);

  const startTime = Date.now();
  const unitsPerTeam = assertion.unitsPerTeam ?? 1;
  const roundResults: RoundResult[] = [];
  const baselines = loadBaselines();
  const baselineSpectacle = baselines[levelId] || assertion.baselineSpectacle;

  console.log(`\n[showcase-validator] Running ${assertion.name} (${assertion.rounds} rounds, ${unitsPerTeam}v${unitsPerTeam})...`);

  for (let i = 0; i < assertion.rounds; i++) {
    const raw = await runOneRound(levelId, unitsPerTeam);
    const behavioral = checkBehavioral(raw, assertion);
    const roundResult: RoundResult = {
      ...raw,
      round: i + 1,
      passedBehavioral: behavioral.passed,
      behavioralFailures: behavioral.failures,
    };
    roundResults.push(roundResult);
    console.log(`  Round ${i + 1}/${assertion.rounds}: spectacle=${roundResult.spectacle.toFixed(1)} winner=${roundResult.winner} ticks=${roundResult.ticks} P1=${behavioral.passed ? "PASS" : "FAIL"}`);
  }

  const spectacles = roundResults.map((r) => r.spectacle);
  const avgSpectacle = spectacles.reduce((s, v) => s + v, 0) / spectacles.length;
  const minSpectacle = Math.min(...spectacles);
  const maxSpectacle = Math.max(...spectacles);

  // P0: 观赏性回归
  const p0Threshold = baselineSpectacle * assertion.regressionThreshold;
  const p0Passed = baselineSpectacle === 0 || avgSpectacle >= p0Threshold;

  // P1: 行为断言（所有 round 都通过才算）
  const p1Failures: string[] = [];
  for (const r of roundResults) {
    if (!r.passedBehavioral) {
      p1Failures.push(`Round ${r.round}: ${r.behavioralFailures.join(", ")}`);
    }
  }
  const p1Passed = p1Failures.length === 0;

  // 平均维度
  const avgDimensions: SpectacleResult["dimensions"] = {
    damageExchange: 0,
    movementComplexity: 0,
    coverInteraction: 0,
    phaseProgression: 0,
    durationOptimal: 0,
    lowHpComeback: 0,
  };
  for (const r of roundResults) {
    for (const key of Object.keys(avgDimensions) as (keyof SpectacleResult["dimensions"])[]) {
      avgDimensions[key] += r.dimensions[key] / roundResults.length;
    }
  }

  // P2: 诊断
  const p2Diagnoses = generateDiagnoses(avgDimensions);

  // 自动建立 baseline（首次运行）
  if (baselineSpectacle === 0 && roundResults.length > 0) {
    baselines[levelId] = Math.round(avgSpectacle * 10) / 10;
    saveBaselines(baselines);
    console.log(`  📊 Baseline established: ${baselines[levelId]}/100 (avg of ${roundResults.length} rounds)`);
  }

  return {
    levelId,
    levelName: assertion.name,
    rounds: assertion.rounds,
    avgSpectacle: Math.round(avgSpectacle * 10) / 10,
    minSpectacle: Math.round(minSpectacle * 10) / 10,
    maxSpectacle: Math.round(maxSpectacle * 10) / 10,
    baselineSpectacle,
    p0Passed,
    p0Threshold: Math.round(p0Threshold * 10) / 10,
    p1Passed,
    p1Failures,
    avgDimensions,
    p2Diagnoses,
    roundResults,
    duration: Date.now() - startTime,
  };
}

export async function validateAllLevels(): Promise<FullReport> {
  const startTime = Date.now();
  const levelIds = Object.keys(assertionsConfig.assertions);

  console.log(`[showcase-validator] Validating ${levelIds.length} showcase levels...\n`);

  const reports: ShowcaseReport[] = [];
  for (const levelId of levelIds) {
    const report = await validateShowcaseLevel(levelId);
    reports.push(report);
  }

  const overallPassed = reports.every((r) => r.p0Passed && r.p1Passed);

  const fullReport: FullReport = {
    timestamp: new Date().toISOString(),
    levels: reports,
    overallPassed,
    totalDuration: Date.now() - startTime,
  };

  // 输出报告
  printReport(fullReport);

  return fullReport;
}

function printReport(report: FullReport): void {
  console.log("\n" + "═".repeat(70));
  console.log("  Showcase Validation Report");
  console.log("═".repeat(70));

  for (const level of report.levels) {
    const p0Icon = level.p0Passed ? "✅" : "❌";
    const p1Icon = level.p1Passed ? "✅" : "❌";
    console.log(`\n  ${level.levelName} (${level.levelId})`);
    console.log(`  ${p0Icon} P0 Spectacle: ${level.avgSpectacle}/100 (baseline: ${level.baselineSpectacle}/100, threshold: ${level.p0Threshold}/100)`);
    console.log(`     min=${level.minSpectacle} max=${level.maxSpectacle}`);
    console.log(`  ${p1Icon} P1 Behavioral: ${level.p1Passed ? "ALL PASS" : level.p1Failures.join("; ")}`);
    console.log(`     Dimensions: DmgEx=${Math.round(level.avgDimensions.damageExchange)} Move=${Math.round(level.avgDimensions.movementComplexity)} Cover=${Math.round(level.avgDimensions.coverInteraction)} Phase=${Math.round(level.avgDimensions.phaseProgression)} Dur=${Math.round(level.avgDimensions.durationOptimal)} Comeback=${Math.round(level.avgDimensions.lowHpComeback)}`);

    if (level.p2Diagnoses.length > 0) {
      console.log(`  💡 P2 Suggestions:`);
      for (const d of level.p2Diagnoses) {
        console.log(`     ${d}`);
      }
    }
  }

  console.log(`\n  Overall: ${report.overallPassed ? "✅ ALL PASS" : "❌ FAILURES DETECTED"}`);
  console.log(`  Duration: ${(report.totalDuration / 1000).toFixed(1)}s`);
  console.log("═".repeat(70) + "\n");
}

// ===== CLI =====
// 用法: 通过 Vite SSR 包装器运行（scripts/run-showcase-validate.mjs）

export async function runCli(args: string[]): Promise<void> {
  const allFlag = args.includes("--all");
  const levelIdx = args.indexOf("--level");
  const levelArg = levelIdx >= 0 ? args[levelIdx + 1] : null;

  if (allFlag) {
    await validateAllLevels();
  } else if (levelArg) {
    const report = await validateShowcaseLevel(levelArg);
    const fullReport: FullReport = {
      timestamp: new Date().toISOString(),
      levels: [report],
      overallPassed: report.p0Passed && report.p1Passed,
      totalDuration: report.duration,
    };
    printReport(fullReport);
  } else {
    // 默认: 显示帮助
    console.log("[showcase-validator] Usage: --all | --level <level_id>");
    console.log("[showcase-validator] Available levels:");
    for (const id of Object.keys(assertionsConfig.assertions)) {
      const a = (assertionsConfig.assertions as Record<string, ShowcaseAssertion>)[id];
      console.log(`  - ${id} (${a.name}, ${a.unitsPerTeam ?? 1}v${a.unitsPerTeam ?? 1})`);
    }
  }
}
